import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

const ThemeContext = createContext();

// Define multiple themes
const themes = {
  default: {
    name: 'Default Blue',
    palette: {
      mode: 'light',
      primary: {
        main: '#1976d2',
        light: '#42a5f5',
        dark: '#1565c0',
      },
      secondary: {
        main: '#9c27b0',
      },
      background: {
        default: '#f5f5f5',
        paper: '#ffffff',
      },
    },
  },
  green: {
    name: 'Nature Green',
    palette: {
      mode: 'light',
      primary: {
        main: '#2e7d32',
        light: '#4caf50',
        dark: '#1b5e20',
      },
      secondary: {
        main: '#ff6f00',
      },
      background: {
        default: '#f1f8e9',
        paper: '#ffffff',
      },
    },
  },
  purple: {
    name: 'Royal Purple',
    palette: {
      mode: 'light',
      primary: {
        main: '#6a1b9a',
        light: '#9c27b0',
        dark: '#4a148c',
      },
      secondary: {
        main: '#00bcd4',
      },
      background: {
        default: '#f3e5f5',
        paper: '#ffffff',
      },
    },
  },
  dark: {
    name: 'Dark Mode',
    palette: {
      mode: 'dark',
      primary: {
        main: '#90caf9',
        light: '#e3f2fd',
        dark: '#42a5f5',
      },
      secondary: {
        main: '#ce93d8',
      },
      background: {
        default: '#121212',
        paper: '#1e1e1e',
      },
    },
  },
  orange: {
    name: 'Sunset Orange',
    palette: {
      mode: 'light',
      primary: {
        main: '#ef6c00',
        light: '#ff9800',
        dark: '#e65100',
      },
      secondary: {
        main: '#0288d1',
      },
      background: {
        default: '#fff3e0',
        paper: '#ffffff',
      },
    },
  },
  teal: {
    name: 'Ocean Teal',
    palette: {
      mode: 'light',
      primary: {
        main: '#00796b',
        light: '#009688',
        dark: '#004d40',
      },
      secondary: {
        main: '#e91e63',
      },
      background: {
        default: '#e0f2f1',
        paper: '#ffffff',
      },
    },
  },
};

export const ThemeContextProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    // Get saved theme from localStorage or use default
    return localStorage.getItem('app-theme') || 'default';
  });

  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem('app-theme', currentTheme);
  }, [currentTheme]);

  const theme = useMemo(() => {
    const selectedTheme = themes[currentTheme] || themes.default;
    return createTheme({
      ...selectedTheme,
      typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h1: { fontWeight: 700 },
        h2: { fontWeight: 700 },
        h3: { fontWeight: 600 },
        h4: { fontWeight: 600 },
        h5: { fontWeight: 600 },
        h6: { fontWeight: 600 },
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              borderRadius: 8,
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 12,
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              borderRadius: 8,
            },
          },
        },
      },
    });
  }, [currentTheme]);

  const value = {
    currentTheme,
    setCurrentTheme,
    themes: Object.keys(themes).map(key => ({
      id: key,
      name: themes[key].name,
      primary: themes[key].palette.primary.main,
      mode: themes[key].palette.mode,
    })),
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeContextProvider');
  }
  return context;
};

export default ThemeContext;
